// ---------------------------------------------------------------------------
// Seed data for datasets.
//
// Extracted from App.tsx — provides initial state for AppContext so the UI
// renders immediately without waiting for API calls.
// ---------------------------------------------------------------------------

import type { Dataset } from '../types';

export const INITIAL_DATASETS: Dataset[] = [
  {
    id: 'call-summary-gold-v1',
    name: 'Call Summary Gold Set v1',
    description:
      'Labeled examples for evaluating call summary quality. Covers technical syncs, business reviews, and 1:1s across a range of note-taking quality levels.',
    tags: ['golden', 'call-summary', 'labeled'],
    uploadedAt: 'Feb 15, 2026',
    records: [
      {
        input:
          'Team Sync — Apr 7\nAttendees: Sarah, Mike, Priya\n- Sprint velocity down 12%\n- No blockers raised',
        expectedScore: 2,
        reasoning:
          'Captures key facts but lacks follow-up actions and owner assignments.',
      },
      {
        input:
          'Quarterly Business Review\nAttendees: all leads\nQ1 ARR: $4.2M (+18% YoY)\nKey wins: Enterprise tier launch, churn reduced to 2.1%\nRisks: EMEA pipeline thin, hiring behind plan\nActions: [1] Sarah to present EMEA plan by Apr 15 [2] Mike to close 3 open reqs by Apr 30',
        expectedScore: 5,
        reasoning:
          'Comprehensive summary with metrics, context, risks, and assigned action items.',
      },
      {
        input: '1:1 with Alex\nDiscussed career goals.',
        expectedScore: 1,
        reasoning: 'Extremely sparse — missing all meaningful content.',
      },
      {
        input:
          'Customer call — Acme Corp\nFeedback: onboarding too slow\nAction: PM to schedule deep-dive',
        expectedScore: 4,
        reasoning:
          'Clear outcome and action item captured; minor detail missing on timeline.',
      },
      {
        input:
          'Design review — checkout flow\nDecision: go with single-page checkout\nOwner: frontend team\nTarget: ship by end of Q2',
        expectedScore: 4,
        reasoning: 'Decision clearly captured with owner and timeline.',
      },
    ],
  },
  {
    id: 'interview-notes-gold-v1',
    name: 'Interview Notes Gold Set v1',
    description:
      'Labeled examples for evaluating structured interview note quality. Used to calibrate judges on hiring signal clarity, evidence specificity, and recommendation completeness.',
    tags: ['golden', 'interview-notes', 'labeled'],
    uploadedAt: 'Feb 18, 2026',
    records: [
      {
        input:
          'Candidate: Jordan M.\nRole: Senior Engineer\nStrength: Strong system design — described a distributed cache handling 50k RPS\nWeakness: Limited cross-functional PM experience\nHiring signal: Strong hire',
        expectedScore: 5,
        reasoning:
          'Detailed, evidence-backed notes with clear hiring recommendation.',
      },
      {
        input: 'Candidate: Lee\nSeemed okay. Would probably hire.',
        expectedScore: 1,
        reasoning: 'No specifics, no evidence, no structured assessment.',
      },
      {
        input:
          'Interview — Data Scientist role\nCoded a decent ML pipeline. Asked good clarifying questions. Weak on deployment experience.',
        expectedScore: 3,
        reasoning:
          'Some specifics but no structured evaluation or hiring recommendation.',
      },
      {
        input:
          'Candidate: Sam T.\nRole: PM\nExcellent stakeholder management examples; weak on metrics and data fluency\nHiring signal: No hire — need stronger analytical skills for this role',
        expectedScore: 4,
        reasoning:
          'Good structure with evidence; recommendation provided but analytical gap only briefly noted.',
      },
    ],
  },
];
